/**
 * Feedback service
 */

import api from './api';

// ==================== 类型定义 ====================

export interface FeedbackItem {
  id: string;
  trace_id: string;
  agent_name: string;
  user_id?: string;
  session_id?: string;
  user_input: string;
  ai_output: string;
  feedback_type: string;
  user_rating?: number;
  is_gold_sample: boolean;
  gold_reason?: string;
  created_at: string;
}

export interface FeedbackStatistics {
  total_feedbacks: number;
  avg_rating: number;
  by_agent: Array<{
    agent_name: string;
    count: number;
    avg_rating: number;
  }>;
  by_type: Record<string, number>;
  by_rating: Record<number, number>;
}

export interface GoldSample {
  trace_id: string;
  agent_name: string;
  user_input: string;
  ai_output: string;
  rating?: number;
  gold_reason?: string;
  created_at: string;
}

export interface FeedbackListResponse {
  success: boolean;
  total: number;
  data: FeedbackItem[];
  error?: string;
}

export interface FeedbackStatsResponse {
  success: boolean;
  data: FeedbackStatistics;
  error?: string;
}

export interface GoldSamplesResponse {
  success: boolean;
  data: GoldSample[];
  error?: string;
}

export interface FeedbackSubmitResponse {
  success: boolean;
  trace_id?: string;
  is_gold_sample?: boolean;
  gold_reason?: string;
  error?: string;
}

const emptyStatistics: FeedbackStatistics = {
  total_feedbacks: 0,
  avg_rating: 0,
  by_agent: [],
  by_type: {},
  by_rating: {},
};

function unwrapData<T>(response: any, fallback: T): T {
  const payload = response?.data ?? response?.result ?? (response?.success === undefined ? response : fallback);
  return payload === null || payload === undefined ? fallback : (payload as T);
}

function asArray<T>(value: any): T[] {
  return Array.isArray(value) ? value : [];
}

function asStatistics(value: any): FeedbackStatistics {
  const payload = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return {
    ...emptyStatistics,
    ...payload,
    by_agent: Array.isArray(payload.by_agent) ? payload.by_agent : [],
    by_type: payload.by_type && typeof payload.by_type === 'object' ? payload.by_type : {},
    by_rating: payload.by_rating && typeof payload.by_rating === 'object' ? payload.by_rating : {},
  };
}

export async function getFeedbackList(params: {
  user_id?: string;
  agent_name?: string;
  trace_id?: string;
  is_gold_sample?: boolean;
  limit?: number;
}): Promise<FeedbackListResponse> {
  const query: Record<string, string> = {};
  if (params.user_id) query.user_id = params.user_id;
  if (params.agent_name) query.agent_name = params.agent_name;
  if (params.trace_id) query.trace_id = params.trace_id;
  if (typeof params.is_gold_sample === 'boolean') query.is_gold_sample = String(params.is_gold_sample);
  if (params.limit) query.limit = String(params.limit);
  const response = await api.get<FeedbackListResponse>('/juben/feedback', query);
  const data = asArray<FeedbackItem>(unwrapData(response, []));
  return {
    ...response,
    success: response?.success !== false,
    data,
    total: response?.total ?? data.length,
  };
}

export async function getFeedbackStatistics(params: {
  agent_name?: string;
  days?: number;
}): Promise<FeedbackStatsResponse> {
  const query: Record<string, string> = {};
  if (params.agent_name) query.agent_name = params.agent_name;
  if (params.days) query.days = String(params.days);
  const response = await api.get<FeedbackStatsResponse>('/juben/feedback/statistics', query);
  return {
    ...response,
    success: response?.success !== false,
    data: asStatistics(unwrapData(response, emptyStatistics)),
  };
}

export async function getGoldSamples(params: {
  agent_name?: string;
  query?: string;
  top_k?: number;
}): Promise<GoldSamplesResponse> {
  const query: Record<string, string> = {};
  if (params.agent_name) query.agent_name = params.agent_name;
  if (params.query) query.query = params.query;
  if (params.top_k) query.top_k = String(params.top_k);
  const response = await api.get<GoldSamplesResponse>('/juben/feedback/gold', query);
  const data = asArray<GoldSample>(unwrapData(response, []));
  return {
    ...response,
    success: response?.success !== false,
    data,
  };
}

export async function submitFeedback(payload: {
  trace_id?: string;
  agent_name: string;
  user_input: string;
  ai_output: string;
  feedback_type: string;
  user_rating?: number;
  user_id?: string;
  session_id?: string;
  metadata?: Record<string, any>;
}): Promise<FeedbackSubmitResponse> {
  const response = await api.post<FeedbackSubmitResponse>('/juben/feedback', payload);
  const data = unwrapData(response, {});
  return {
    ...response,
    ...(data && typeof data === 'object' && !Array.isArray(data) ? data : {}),
    success: response?.success !== false,
  };
}

export async function submitRefinement(payload: {
  trace_id: string;
  agent_name: string;
  original_text: string;
  modified_text: string;
  user_id?: string;
  session_id?: string;
  context?: string;
}): Promise<FeedbackSubmitResponse> {
  const response = await api.post<FeedbackSubmitResponse>('/juben/feedback/refinement', payload);
  const data = unwrapData(response, {});
  return {
    ...response,
    ...(data && typeof data === 'object' && !Array.isArray(data) ? data : {}),
    success: response?.success !== false,
  };
}
